import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { C, font } from "../tokens";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { applySeo } from "../lib/seo";
import { posts, categories } from "../data/blog";

function fmtDate(d) {
  try {
    return new Date(d + "T12:00:00").toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function PostCard({ p, featured = false }) {
  const [hover, setHover] = useState(false);
  const cat = (p.categories && p.categories[0]) || null;
  return (
    <Link
      to={`/${p.slug}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        flexDirection: featured ? "column" : "column",
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 18,
        overflow: "hidden",
        textDecoration: "none",
        color: C.text,
        boxShadow: hover
          ? "0 20px 44px rgba(10,15,30,0.12)"
          : "0 6px 20px rgba(10,15,30,0.05)",
        transform: hover ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.25s cubic-bezier(.16,1,.3,1), box-shadow 0.25s",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: featured ? "16 / 8" : "16 / 9",
          overflow: "hidden",
          background: C.bgDeep,
        }}
      >
        <img
          src={p.cover}
          alt={p.title}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: hover ? "scale(1.05)" : "scale(1)",
            transition: "transform 0.5s cubic-bezier(.16,1,.3,1)",
          }}
        />
        {cat && (
          <span
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              fontFamily: font,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.3,
              color: "#fff",
              background: "rgba(5,96,255,0.92)",
              padding: "5px 11px",
              borderRadius: 999,
              backdropFilter: "blur(4px)",
            }}
          >
            {cat.name}
          </span>
        )}
      </div>
      <div style={{ padding: featured ? "26px 28px 28px" : "20px 22px 22px", display: "flex", flexDirection: "column", flex: 1 }}>
        <h3
          style={{
            fontFamily: font,
            fontSize: featured ? 25 : 18,
            fontWeight: 800,
            letterSpacing: -0.4,
            lineHeight: 1.25,
            margin: "0 0 10px",
            color: C.text,
          }}
        >
          {p.title}
        </h3>
        <p
          style={{
            fontFamily: font,
            fontSize: featured ? 15.5 : 14,
            lineHeight: 1.6,
            color: C.muted,
            margin: "0 0 16px",
            display: "-webkit-box",
            WebkitLineClamp: featured ? 3 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {p.excerpt}
        </p>
        <div
          style={{
            marginTop: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontFamily: font,
            fontSize: 12.5,
            color: C.muted,
          }}
        >
          <span>{fmtDate(p.date)}</span>
          <span style={{ color: C.accent, fontWeight: 800 }}>Ler artigo →</span>
        </div>
      </div>
    </Link>
  );
}

const PER_PAGE = 9;

// Botão de página reutilizável
function PageBtn({ children, active = false, disabled = false, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 40,
        height: 40,
        padding: "0 12px",
        borderRadius: 11,
        fontFamily: font,
        fontSize: 14,
        fontWeight: 700,
        cursor: disabled ? "default" : "pointer",
        border: `1.5px solid ${active ? C.accent : C.border}`,
        background: active ? C.accent : C.surface,
        color: active ? "#fff" : disabled ? "#B7C0D0" : C.text,
        opacity: disabled ? 0.55 : 1,
        transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

// Gera a sequência de páginas com reticências (ex: 1 … 4 5 6 … 12)
function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}

export default function Conteudo() {
  const [active, setActive] = useState("todos");
  const [page, setPage] = useState(1);
  const listTopRef = useRef(null);

  useEffect(() => {
    applySeo({
      title: "Conteúdo IMOBMEET: blog do mercado imobiliário | IMOBMEET",
      description:
        "Artigos, guias e tendências sobre CRM, vendas, marketing e gestão para incorporadoras, loteadoras e construtoras. O blog do IMOBMEET.",
      keywords:
        "blog imobiliário, crm imobiliário, gestão de vendas, marketing imobiliário, loteadoras, incorporadoras",
      path: "/conteudo",
    });
    window.scrollTo(0, 0);
  }, []);

  const filtered =
    active === "todos"
      ? posts
      : posts.filter((p) => (p.categories || []).some((c) => c.slug === active));

  // Card destaque só aparece na primeira página da aba "Todos"
  const useFeatured = active === "todos";
  const featured = useFeatured ? filtered[0] : null;
  const listForGrid = useFeatured ? filtered.slice(1) : filtered;

  const totalPages = Math.max(1, Math.ceil(listForGrid.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pageItems = listForGrid.slice((current - 1) * PER_PAGE, current * PER_PAGE);
  const showFeatured = featured && current === 1;

  const changeCategory = (slug) => {
    setActive(slug);
    setPage(1);
  };

  const goToPage = (p) => {
    setPage(p);
    // Rola até o topo da lista (não ao topo absoluto da página)
    if (listTopRef.current) {
      const y = listTopRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <>
      <Navbar />
      {/* Hero */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #0B1733 0%, #0A1230 100%)",
          padding: "116px 28px 56px",
        }}
      >
        <div className="grid-bg" style={{ position: "absolute", inset: 0, opacity: 0.25, pointerEvents: "none" }} />
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -60,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(5,96,255,0.30), transparent 65%)",
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <div
            style={{
              fontFamily: font,
              fontSize: 12.5,
              fontWeight: 800,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "#8FB3E6",
              marginBottom: 14,
            }}
          >
            Conteúdo
          </div>
          <h1
            style={{
              fontFamily: font,
              fontSize: "clamp(32px, 5vw, 50px)",
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: -1.4,
              color: "#fff",
              margin: "0 0 16px",
              maxWidth: 820,
            }}
          >
            Inteligência para vender mais no mercado imobiliário
          </h1>
          <p
            style={{
              fontFamily: font,
              fontSize: 17,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.78)",
              margin: 0,
              maxWidth: 640,
            }}
          >
            Guias práticos, tendências e estratégias de CRM, vendas, marketing e
            gestão para incorporadoras, loteadoras e construtoras.
          </p>
        </div>
      </section>

      {/* Conteúdo */}
      <section style={{ background: C.bgAlt, padding: "40px 28px 88px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div ref={listTopRef} />

          {/* Filtro de categorias — chips no desktop */}
          <div
            className="cat-chips"
            style={{ flexWrap: "wrap", gap: 9, marginBottom: 34 }}
          >
            {[{ name: "Todos", slug: "todos" }, ...categories].map((c) => {
              const on = active === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => changeCategory(c.slug)}
                  style={{
                    fontFamily: font,
                    fontSize: 13,
                    fontWeight: 700,
                    padding: "8px 16px",
                    borderRadius: 999,
                    cursor: "pointer",
                    border: `1.5px solid ${on ? C.accent : C.border}`,
                    background: on ? C.accent : C.surface,
                    color: on ? "#fff" : C.muted,
                    transition: "all 0.15s",
                  }}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          {/* Filtro de categorias — dropdown no mobile */}
          <div className="cat-select" style={{ marginBottom: 26 }}>
            <label
              style={{
                display: "block",
                fontFamily: font,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: C.muted,
                marginBottom: 8,
              }}
            >
              Filtrar por categoria
            </label>
            <div style={{ position: "relative" }}>
              <select
                value={active}
                onChange={(e) => changeCategory(e.target.value)}
                style={{
                  width: "100%",
                  appearance: "none",
                  WebkitAppearance: "none",
                  fontFamily: font,
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.text,
                  background: C.surface,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 12,
                  padding: "13px 44px 13px 16px",
                  cursor: "pointer",
                }}
              >
                <option value="todos">Todos os conteúdos</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: C.accent,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p style={{ fontFamily: font, color: C.muted }}>Nenhum artigo nesta categoria ainda.</p>
          ) : (
            <>
              {showFeatured && (
                <div style={{ marginBottom: 28 }}>
                  <PostCard p={featured} featured />
                </div>
              )}
              <div
                className="blog-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: 24,
                }}
              >
                {pageItems.map((p) => (
                  <PostCard key={p.slug} p={p} />
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 48,
                  }}
                >
                  <PageBtn disabled={current === 1} onClick={() => goToPage(current - 1)}>
                    ←
                  </PageBtn>
                  {pageRange(current, totalPages).map((p, i) =>
                    p === "…" ? (
                      <span
                        key={`dots-${i}`}
                        style={{ fontFamily: font, color: C.muted, padding: "0 4px", fontWeight: 700 }}
                      >
                        …
                      </span>
                    ) : (
                      <PageBtn key={p} active={p === current} onClick={() => goToPage(p)}>
                        {p}
                      </PageBtn>
                    )
                  )}
                  <PageBtn disabled={current === totalPages} onClick={() => goToPage(current + 1)}>
                    →
                  </PageBtn>
                </div>
              )}

              {/* Contador */}
              <p
                style={{
                  textAlign: "center",
                  fontFamily: font,
                  fontSize: 13,
                  color: C.muted,
                  marginTop: 18,
                }}
              >
                {filtered.length} {filtered.length === 1 ? "artigo" : "artigos"}
                {totalPages > 1 ? ` · página ${current} de ${totalPages}` : ""}
              </p>
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
